/**
 * UploadZone Component
 * ====================
 * Drag and drop file upload zone.
 */

import React, { useRef, useState } from 'react';

interface UploadZoneProps {
    accept: string;
    onDrop: (files: File[]) => void;
    label: string;
}

const UploadZone: React.FC<UploadZoneProps> = ({ accept, onDrop, label }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            onDrop(files);
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            onDrop(files);
        }
        // Reset input
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    return (
        <div
            className={`upload-zone ${isDragOver ? 'upload-zone--active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            <div className="upload-zone__icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
            </div>
            <p className="upload-zone__label">{label}</p>
            <p className="upload-zone__hint">or click to browse</p>
        </div>
    );
};

export default UploadZone;
